/**
 * OwnCifOverrideModal — iter 82 (Bloque 3.j).
 *
 * Se abre cuando `POST /api/invoices` responde 409 `own_invoice` porque el
 * CIF/NIF que el OCR extrajo coincide con el CIF fiscal del propio tenant
 * (auto-factura como gasto — no permitida).
 *
 * El usuario elige entre:
 *   1. Un proveedor ya existente de su directorio (autocomplete
 *      `GET /api/vendors/suggestions`).
 *   2. Otro proveedor nuevo (input libre de nombre + CIF).
 *
 * Al confirmar reintenta el POST con el nuevo `vendor` + `vendor_tax_id`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Building2, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function OwnCifOverrideModal({
  open,
  onOpenChange,
  detectedCif,
  onConfirm,
  lang = "es",
}) {
  // "existing" | "other"
  const [mode, setMode] = useState("existing");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [pickedVendor, setPickedVendor] = useState(null); // {name, tax_id}
  const [otherName, setOtherName] = useState("");
  const [otherCif, setOtherCif] = useState("");
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  // Reset cuando el modal se cierra.
  useEffect(() => {
    if (!open) {
      setMode("existing");
      setQuery("");
      setSuggestions([]);
      setPickedVendor(null);
      setOtherName("");
      setOtherCif("");
      setBusy(false);
    }
  }, [open]);

  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/vendors/suggestions?q=${encodeURIComponent(q || "")}&limit=8`,
        );
        setSuggestions(data?.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    }, 180);
  }, []);

  // Carga inicial cuando se abre en modo "existing".
  useEffect(() => {
    if (open && mode === "existing") fetchSuggestions("");
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, mode, fetchSuggestions]);

  const isValid = mode === "existing"
    ? !!pickedVendor
    : otherName.trim().length > 0;

  const handleConfirm = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const vendor = mode === "existing" ? pickedVendor.name : otherName.trim();
      const taxIdRaw = mode === "existing" ? pickedVendor.tax_id : otherCif.trim();
      // El usuario expresamente ha decidido usar otro proveedor → NUNCA
      // enviamos el CIF propio; si el otherCif está vacío, mandamos null.
      const taxId = (taxIdRaw || "").toUpperCase().replace(/\s+/g, "") || null;
      await onConfirm({ vendor, vendor_tax_id: taxId });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="own-cif-override-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            {lang === "es"
              ? "Este CIF/NIF coincide con el tuyo"
              : "This tax ID matches your own"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {lang === "es" ? (
              <>
                No puedes registrar tu propia factura como <b>gasto</b>. El OCR
                extrajo <span className="font-mono">{detectedCif || "—"}</span>,
                que es tu CIF fiscal. Elige un proveedor real de tu directorio
                o escribe uno nuevo y guardamos el gasto con esos datos.
              </>
            ) : (
              <>
                You can't register your own invoice as an <b>expense</b>. The OCR
                extracted <span className="font-mono">{detectedCif || "—"}</span>,
                which is your fiscal tax ID. Pick a real vendor from your
                directory or type a new one — we'll save the expense with that data.
              </>
            )}
          </p>

          {/* Mode switcher */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setMode("existing"); setPickedVendor(null); }}
              data-testid="own-cif-mode-existing"
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                mode === "existing"
                  ? "border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)] font-medium"
                  : "border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
              }`}
            >
              {lang === "es" ? "De mi directorio" : "From my directory"}
            </button>
            <button
              type="button"
              onClick={() => { setMode("other"); setSuggestions([]); }}
              data-testid="own-cif-mode-other"
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                mode === "other"
                  ? "border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)] font-medium"
                  : "border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]"
              }`}
            >
              {lang === "es" ? "Otro proveedor" : "Other vendor"}
            </button>
          </div>

          {mode === "existing" ? (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Buscar proveedor" : "Search vendor"}
              </Label>
              <Input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  fetchSuggestions(e.target.value);
                }}
                placeholder={lang === "es" ? "Nombre o CIF…" : "Name or tax ID…"}
                data-testid="own-cif-vendor-search"
              />
              <div className="max-h-56 overflow-y-auto space-y-1 border border-[var(--border-color)] rounded-md p-1">
                {suggestions.length === 0 && (
                  <p className="text-xs text-[var(--text-muted)] px-2 py-3 text-center">
                    {lang === "es"
                      ? "No hay proveedores. Cambia a \"Otro\" para escribir uno."
                      : "No vendors found. Switch to \"Other\" to type one."}
                  </p>
                )}
                {suggestions.map((s, idx) => {
                  const isSel = pickedVendor && pickedVendor.name === s.name && pickedVendor.tax_id === (s.tax_id || null);
                  return (
                    <button
                      key={`${s.name}-${s.tax_id || idx}`}
                      type="button"
                      onClick={() => setPickedVendor({ name: s.name, tax_id: s.tax_id || null })}
                      data-testid={`own-cif-vendor-option-${idx}`}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                        isSel
                          ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                          : "hover:bg-[var(--surface-hover)] text-[var(--text-primary)]"
                      }`}
                    >
                      <Building2 size={14} className="text-[var(--text-muted)] shrink-0" />
                      <span className="flex-1 truncate">{s.name}</span>
                      {s.tax_id && (
                        <span className="font-mono text-xs text-[var(--text-muted)]">{s.tax_id}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                  {lang === "es" ? "Nombre del proveedor" : "Vendor name"}
                </Label>
                <Input
                  autoFocus
                  value={otherName}
                  onChange={(e) => setOtherName(e.target.value)}
                  data-testid="own-cif-other-name"
                  className="mt-1.5"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                  {lang === "es" ? "CIF/NIF (opcional)" : "Tax ID (optional)"}
                </Label>
                <Input
                  value={otherCif}
                  onChange={(e) => setOtherCif(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                  data-testid="own-cif-other-taxid"
                  placeholder="B12345678"
                  className="font-mono mt-1.5"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="own-cif-cancel-btn"
          >
            <X size={14} className="mr-1.5" />
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || busy}
            data-testid="own-cif-confirm-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {busy
              ? "..."
              : (lang === "es" ? "Guardar como gasto" : "Save as expense")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
